import React, { useState, useRef, useCallback } from 'react';

const MAX_IMAGE_WIDTH = 800;
const MAX_IMAGE_HEIGHT = 600;
const IMAGE_QUALITY = 0.7;

const ImageUpload = ({ imageData, onImageReady }) => {
  const [imagePreview, setImagePreview] = useState(imageData || null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const processImage = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > MAX_IMAGE_WIDTH) {
              height = Math.round((height * MAX_IMAGE_WIDTH) / width);
              width = MAX_IMAGE_WIDTH;
            }
          } else {
            if (height > MAX_IMAGE_HEIGHT) {
              width = Math.round((width * MAX_IMAGE_HEIGHT) / height);
              height = MAX_IMAGE_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_QUALITY);
          resolve(dataUrl);
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        
        img.src = event.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }, []);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setProcessing(true);
      
      // Create preview immediately
      const previewURL = URL.createObjectURL(file);
      setImagePreview(previewURL);
      
      // Process image
      const processedImage = await processImage(file);
      
      // Clean up object URL
      URL.revokeObjectURL(previewURL);
      
      // Set processed image as preview and notify parent
      setImagePreview(processedImage);
      onImageReady(processedImage);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Failed to process image. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [processImage, onImageReady]);

  const triggerFileInput = useCallback(() => {
    if (!processing) {
      fileInputRef.current?.click();
    }
  }, [processing]);

  const removeImage = useCallback(() => {
    setImagePreview(null);
    onImageReady(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onImageReady]);

  return (
    <div className="flex justify-center">
      <div className="relative">
        <div 
          className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors ${
            processing ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={triggerFileInput}
          style={{ width: '200px', height: '150px' }}
        >
          {processing ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand"></div>
              <p className="mt-2 text-sm text-gray-500">Processing image...</p>
            </div>
          ) : imagePreview ? (
            <div className="relative h-full">
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="max-h-full max-w-full mx-auto object-contain"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage();
                }}
                className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">Click to upload image</p>
              <p className="text-xs text-gray-400 mt-1">Max size: 800x600</p>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
          disabled={processing}
        />
      </div>
    </div>
  );
};

export default ImageUpload;